import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";
import type {
  AsyncTaskRecord,
  EstimateJobDetail,
  TenderOutcome,
  Variant,
} from "./types";

const qk = {
  jobs: ["est", "jobs"] as const,
  job: (id: string) => ["est", "job", id] as const,
  task: (id: string) => ["est", "task", id] as const,
  coefficientSets: ["est", "coefficient-sets"] as const,
  catalogs: ["est", "catalogs"] as const,
  changeLog: (jobId: string) => ["est", "change-log", jobId] as const,
};

const TERMINAL = ["success", "failure", "cancelled"];

export const useStartEstimateJob = () =>
  useMutation({
    mutationFn: (vars: {
      files: File[];
      coefficient_set?: string;
      rms_catalog?: string;
      bcj_catalog?: string;
      offer_deadline?: string;
      tender_url?: string;
    }) => api.startEstimateJob(vars.files, vars),
  });

export const useTaskPolling = (taskId: string | null) =>
  useQuery<AsyncTaskRecord>({
    queryKey: qk.task(taskId ?? ""),
    queryFn: () => api.getTask(taskId!),
    enabled: !!taskId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && TERMINAL.includes(s) ? false : 1500;
    },
  });

export const useJobs = () => useQuery({ queryKey: qk.jobs, queryFn: api.listJobs });

export const useJob = (id: string | undefined) =>
  useQuery<EstimateJobDetail>({
    queryKey: qk.job(id ?? ""),
    queryFn: () => api.getJob(id!),
    enabled: !!id,
    // Poll while the analysis is still running so the result page fills in live.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "ready" || s === "failed" ? false : 2000;
    },
  });

export const useRecomputeJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, string | number>) => api.recomputeJob(id, payload),
    onSuccess: (data) => qc.setQueryData(qk.job(id), data),
  });
};

export const useExpandNormsJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.expandNormsJob(id),
    onSuccess: (data) => qc.setQueryData(qk.job(id), data),
  });
};

export const useShareJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; remove?: boolean; canEdit?: boolean }) =>
      api.shareJob(id, v.userId, v.remove, v.canEdit),
    onSuccess: (data) => qc.setQueryData(qk.job(id), data),
  });
};

export const useShareableUsers = (enabled: boolean) =>
  useQuery({
    queryKey: ["estimating", "shareable-users"],
    queryFn: api.getShareableUsers,
    enabled,
  });

export const useCompleteJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (completed: boolean) => api.completeJob(id, completed),
    onSuccess: (data) => {
      qc.setQueryData(qk.job(id), data);
      qc.invalidateQueries({ queryKey: qk.jobs });
    },
  });
};

export const useAbandonJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (abandoned: boolean) => api.abandonJob(id, abandoned),
    onSuccess: (data) => {
      qc.setQueryData(qk.job(id), data);
      qc.invalidateQueries({ queryKey: qk.jobs });
    },
  });
};

export const useSubmitJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      submitted: boolean;
      variant?: Variant;
      amount?: string;
    }) => api.submitJob(id, v.submitted, { variant: v.variant, amount: v.amount }),
    onSuccess: (data) => {
      qc.setQueryData(qk.job(id), data);
      qc.invalidateQueries({ queryKey: qk.jobs });
      // Wariant mógł się zmienić razem ze złożeniem — marże scenariuszy są
      // liczone od naszej wartości, więc ich snapshoty trzeba odświeżyć.
      qc.invalidateQueries({ queryKey: ["est", "scenarios", id] });
    },
  });
};

/** Wynik przetargu (wygrany / przegrane / anulowane); null cofa wynik. */
export const useSetJobOutcome = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (outcome: TenderOutcome | null) => api.setJobOutcome(id, outcome),
    onSuccess: (data) => {
      qc.setQueryData(qk.job(id), data);
      qc.invalidateQueries({ queryKey: qk.jobs });
    },
  });
};

export const useSetJobBidVariant = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variant: Variant) => api.setJobBidVariant(id, variant),
    onSuccess: (data) => {
      qc.setQueryData(qk.job(id), data);
      // Scenariusze podwykonawcze liczą marżę od NASZEJ wartości — po zmianie
      // wariantu ich zapisane snapshoty są nieaktualne.
      qc.invalidateQueries({ queryKey: ["est", "scenarios", id] });
      qc.invalidateQueries({ queryKey: qk.jobs });
    },
  });
};

export const useSetJobSubmittedAmount = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (amount: string) => api.setJobSubmittedAmount(id, amount),
    onSuccess: (data) => {
      qc.setQueryData(qk.job(id), data);
      qc.invalidateQueries({ queryKey: qk.jobs });
    },
  });
};

export const useSetJobNote = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.setJobNote(id, note),
    onSuccess: (data) => qc.setQueryData(qk.job(id), data),
  });
};

export const useSetJobTenderInfo = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { offer_deadline?: string | null; tender_url?: string }) =>
      api.setJobTenderInfo(id, v),
    onSuccess: (data) => {
      qc.setQueryData(qk.job(id), data);
      qc.invalidateQueries({ queryKey: qk.jobs });
    },
  });
};

export const useMatchInput = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; entryId: string | null }) => api.matchInput(v.inputId, v.entryId),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useMatchPosition = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; entryId: string | null }) => api.matchPosition(v.positionId, v.entryId),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetInvestorSupplied = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; value: boolean }) => api.setInvestorSupplied(v.inputId, v.value),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetManualPrice = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; price: string | null }) => api.setManualPrice(v.inputId, v.price),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetPositionCategory = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; category: string }) =>
      api.setPositionCategory(v.positionId, v.category),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetPositionManualPrice = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; price: string | null }) =>
      api.setPositionManualPrice(v.positionId, v.price),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetPositionQuantity = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; quantity: string | null }) =>
      api.setPositionQuantity(v.positionId, v.quantity),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetPositionInvestorSupplied = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; value: boolean }) =>
      api.setPositionInvestorSupplied(v.positionId, v.value),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetInputExcluded = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; excluded: boolean }) =>
      api.setInputExcluded(v.inputId, v.excluded),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetInputNorm = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; normTotal: string | null }) =>
      api.setInputNorm(v.inputId, v.normTotal),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

/** Nakłady R/M/S dopisywane ręcznie pod pozycją dodaną ręcznie. */
export const useManualInputs = (jobId: string) => {
  const qc = useQueryClient();
  const onSuccess = (job: EstimateJobDetail) => qc.setQueryData(qk.job(jobId), job);
  return {
    create: useMutation({
      mutationFn: (v: { positionId: string; payload: api.ManualInputPayload }) =>
        api.createManualInput(v.positionId, v.payload),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (inputId: string) => api.deleteManualInput(inputId),
      onSuccess,
    }),
  };
};

export const useSetInputKind = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      inputId: string;
      kind: "labor" | "material" | "equipment";
    }) => api.setInputKind(v.inputId, v.kind),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetPositionWholeMarkup = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; mode: "material" | "labor" }) =>
      api.setPositionWholeMarkup(v.positionId, v.mode),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetPositionBasis = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; basis: string }) =>
      api.setPositionBasis(v.positionId, v.basis),
    onSuccess: ({ data }) => {
      qc.setQueryData(qk.job(jobId), data);
      qc.invalidateQueries({ queryKey: qk.changeLog(jobId) });
    },
  });
};

export const useSetPositionSoilCategory = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; category: string }) =>
      api.setPositionSoilCategory(v.positionId, v.category),
    onSuccess: ({ data }) => {
      qc.setQueryData(qk.job(jobId), data);
      qc.invalidateQueries({ queryKey: qk.changeLog(jobId) });
    },
  });
};

export const useSetJobSoilCategory = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { category: string; positionIds?: string[] }) =>
      api.setJobSoilCategory(jobId, v.category, v.positionIds),
    onSuccess: ({ data }) => {
      qc.setQueryData(qk.job(jobId), data);
      qc.invalidateQueries({ queryKey: qk.changeLog(jobId) });
    },
  });
};

/** Dry-run kaskady kategorii gruntu — diff „przed / po” bez zapisu. */
export const useJobSoilPreview = (jobId: string) =>
  useMutation({
    mutationFn: (v: { category: string; positionIds?: string[] }) =>
      api.previewJobSoilCategory(jobId, v.category, v.positionIds),
  });

export const useChangeLog = (jobId: string, enabled = true) =>
  useQuery({
    queryKey: qk.changeLog(jobId),
    queryFn: () => api.listChangeLog(jobId),
    enabled: enabled && !!jobId,
  });

export const useRevertChange = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { log_id?: string; batch_id?: string }) =>
      api.revertChange(jobId, payload),
    onSuccess: ({ data }) => {
      qc.setQueryData(qk.job(jobId), data);
      qc.invalidateQueries({ queryKey: qk.changeLog(jobId) });
    },
  });
};

/** Podpowiedzi z katalogu norm (edytor podstawy) — od 3 znaków. */
export const useNormSearch = (q: string) =>
  useQuery({
    queryKey: ["est", "norms", q],
    queryFn: () => api.searchNormPositions(q),
    enabled: q.trim().length >= 3,
  });

export const useSetPositionDescription = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; description: string }) =>
      api.setPositionDescription(v.positionId, v.description),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetInputDescription = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; description: string }) =>
      api.setInputDescription(v.inputId, v.description),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetPositionComment = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; comment: string }) =>
      api.setPositionComment(v.positionId, v.comment),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useSetInputComment = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; comment: string }) =>
      api.setInputComment(v.inputId, v.comment),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

/** Pozycje dopisywane ręcznie (dodaj / popraw / usuń) — każda zmiana przelicza wycenę. */
export const useManualPositions = (jobId: string) => {
  const qc = useQueryClient();
  const onSuccess = (job: EstimateJobDetail) => qc.setQueryData(qk.job(jobId), job);
  return {
    create: useMutation({
      mutationFn: (v: { estimateId: string; payload: api.ManualPositionPayload }) =>
        api.createManualPosition(v.estimateId, v.payload),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (v: { positionId: string; payload: api.ManualPositionPayload }) =>
        api.updateManualPosition(v.positionId, v.payload),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (positionId: string) => api.deleteManualPosition(positionId),
      onSuccess,
    }),
  };
};

export const useSetRouteLength = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      estimateId: string;
      route_open_trench_m?: string | null;
      route_bore_m?: string | null;
      route_length_m?: string | null;
    }) =>
      api.setRouteLength(v.estimateId, {
        route_open_trench_m: v.route_open_trench_m,
        route_bore_m: v.route_bore_m,
        route_length_m: v.route_length_m,
      }),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useAiPriceInput = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { inputId: string; clear?: boolean }) =>
      api.aiPriceInput(v.inputId, v.clear),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useAiPricePosition = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; clear?: boolean }) =>
      api.aiPricePosition(v.positionId, v.clear),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useAiPriceUnmatched = () =>
  useMutation({ mutationFn: (jobId: string) => api.aiPriceUnmatched(jobId) });

export const useAiReviewRun = (jobId: string) =>
  useMutation({ mutationFn: () => api.aiReviewRun(jobId) });

export const useAiReviewClear = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.aiReviewClear(jobId),
    onSuccess: (job) => qc.setQueryData(qk.job(jobId), job),
  });
};

export const useCatalogEntrySearch = (catalogId: string | null, kind: string, q: string) =>
  useQuery({
    queryKey: ["est", "entries", catalogId ?? "", kind, q],
    queryFn: () => api.searchCatalogEntries(catalogId!, q, kind || undefined),
    enabled: !!catalogId && q.trim().length >= 2,
  });

export const useCoefficientSets = () =>
  useQuery({ queryKey: qk.coefficientSets, queryFn: api.listCoefficientSets, staleTime: 5 * 60_000 });

// ── Subcontractor pricing ────────────────────────────────────────────────────

export const useFrameworkAgreements = () =>
  useQuery({
    queryKey: ["est", "framework-agreements"],
    queryFn: api.listFrameworkAgreements,
    staleTime: 5 * 60_000,
  });

export const useScenarios = (jobId: string | undefined) =>
  useQuery({
    queryKey: ["est", "scenarios", jobId ?? ""],
    queryFn: () => api.listScenarios(jobId!),
    enabled: !!jobId,
  });

export const useScenarioLineItems = (sid: string | null) =>
  useQuery({
    queryKey: ["est", "line-items", sid ?? ""],
    queryFn: () => api.scenarioLineItems(sid!),
    enabled: !!sid,
  });

export const useScenarioContractors = (sid: string | null) =>
  useQuery({
    queryKey: ["est", "sc-contractors", sid ?? ""],
    queryFn: () => api.scenarioContractors(sid!),
    enabled: !!sid,
  });

export const useScenarioMutations = (jobId: string) => {
  const qc = useQueryClient();
  const onSuccess = () =>
    qc.invalidateQueries({ queryKey: ["est", "scenarios", jobId] });
  return {
    create: useMutation({
      mutationFn: (v: { name: string; agreement_ids: string[] }) =>
        api.createScenario(jobId, v.name, v.agreement_ids),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (v: {
        sid: string;
        name?: string;
        agreement_ids?: string[];
        ai_rule_text?: string;
      }) =>
        api.updateScenario(v.sid, {
          name: v.name,
          agreement_ids: v.agreement_ids,
          ai_rule_text: v.ai_rule_text,
        }),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (sid: string) => api.deleteScenario(sid),
      onSuccess,
    }),
    aiMatch: useMutation({
      mutationFn: (sid: string) => api.aiMatchScenario(sid),
      onSuccess,
    }),
    addAssignment: useMutation({
      mutationFn: (v: {
        sid: string;
        line_item_type_id: string;
        covered_position_ids: string[];
        quantity?: string;
        use_high_priority?: boolean;
      }) =>
        api.addAssignment(v.sid, {
          line_item_type_id: v.line_item_type_id,
          covered_position_ids: v.covered_position_ids,
          quantity: v.quantity,
          use_high_priority: v.use_high_priority,
        }),
      onSuccess,
    }),
    patchAssignment: useMutation({
      mutationFn: (v: {
        aid: string;
        quantity?: string;
        rate?: string;
        use_high_priority?: boolean;
        is_alternative?: boolean;
        note?: string;
      }) => api.patchAssignment(v.aid, v),
      onSuccess,
    }),
    deleteAssignment: useMutation({
      mutationFn: (aid: string) => api.deleteAssignment(aid),
      onSuccess,
    }),
    setCoverage: useMutation({
      mutationFn: (v: {
        aid: string;
        position_id: string;
        fraction?: string;
        amount?: string;
      }) => api.setCoverage(v.aid, v.position_id, v.fraction, v.amount),
      onSuccess,
    }),
    removeCoverage: useMutation({
      mutationFn: (v: { aid: string; position_id: string }) =>
        api.removeCoverage(v.aid, v.position_id),
      onSuccess,
    }),
    buildLocation: useMutation({
      mutationFn: (v: {
        sid: string;
        lat: string | null;
        lng: string | null;
        label?: string;
      }) => api.setBuildLocation(v.sid, { lat: v.lat, lng: v.lng, label: v.label }),
      onSuccess: () => {
        onSuccess();
        qc.invalidateQueries({ queryKey: ["est", "sc-contractors"] });
      },
    }),
    addBase: useMutation({
      mutationFn: (v: { contractorId: string; label: string; city: string }) =>
        api.addContractorBase(v.contractorId, { label: v.label, city: v.city }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["est", "sc-contractors"] }),
    }),
    deleteBase: useMutation({
      mutationFn: (baseId: string) => api.deleteContractorBase(baseId),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["est", "sc-contractors"] }),
    }),
  };
};

export const useCatalogs = () =>
  useQuery({ queryKey: qk.catalogs, queryFn: api.listCatalogs, staleTime: 5 * 60_000 });

// ── Nasza baza cen ───────────────────────────────────────────────────────────

const qkCustomPrices = ["est", "custom-prices"] as const;

export const useCustomPrices = (q = "", kind = "", enabled = true) =>
  useQuery({
    queryKey: [...qkCustomPrices, q, kind],
    queryFn: () => api.listCustomPrices(q, kind),
    enabled,
  });

export const useCustomPriceMutations = () => {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: qkCustomPrices });
  return {
    create: useMutation({
      mutationFn: (payload: api.CustomPricePayload) =>
        api.createCustomPrice(payload),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: api.CustomPricePayload }) =>
        api.updateCustomPrice(v.id, v.payload),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteCustomPrice(id),
      onSuccess,
    }),
  };
};

// ── Baza wiedzy PRZ-ARON ─────────────────────────────────────────────────────

const qkKnowledge = ["est", "knowledge-notes"] as const;

export const useKnowledgeNotes = (enabled = true) =>
  useQuery({ queryKey: qkKnowledge, queryFn: api.listKnowledgeNotes, enabled });

export const useKnowledgeNoteMutations = () => {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: qkKnowledge });
  return {
    create: useMutation({
      mutationFn: (body: string) => api.createKnowledgeNote(body),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; body?: string; is_active?: boolean }) =>
        api.updateKnowledgeNote(v.id, { body: v.body, is_active: v.is_active }),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteKnowledgeNote(id),
      onSuccess,
    }),
  };
};

// ── Zestawienie materiałów / sprzętu + rozeznanie rynku ──────────────────────

const qkSummaries = (jobId: string, threshold: string) =>
  ["est", "summaries", jobId, threshold] as const;

export const useJobSummaries = (jobId: string, threshold: string, enabled = true) =>
  useQuery({
    queryKey: qkSummaries(jobId, threshold),
    queryFn: () => api.getJobSummaries(jobId, threshold),
    enabled: enabled && !!jobId,
  });

export const useMaterialOfferScan = (jobId: string) => {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["est", "summaries", jobId] });
  return {
    scan: useMutation({
      mutationFn: (keys: string[]) => api.scanMaterialOffers(jobId, keys),
    }),
    clear: useMutation({
      mutationFn: () => api.clearMaterialOffers(jobId),
      onSuccess: invalidate,
    }),
    invalidate,
  };
};

export const useRescanOutages = () =>
  useMutation({ mutationFn: (jobId: string) => api.rescanOutages(jobId) });

export const useRescanInvestorSupply = () =>
  useMutation({ mutationFn: (jobId: string) => api.rescanInvestorSupply(jobId) });

export const useRunDeviceCheck = () =>
  useMutation({ mutationFn: (jobId: string) => api.runDeviceCheck(jobId) });

// ── obmiar z rysunku ─────────────────────────────────────────────────────────

const takeoffRulesQk = ["est", "takeoff-rules"] as const;

/** Rysunek → przedmiar → kosztorys. Zwraca to samo co start wyceny z pliku
 *  ({task_id, job_id}), bo od tego momentu to jest zwykły kosztorys. */
export const useStartTakeoffJob = () =>
  useMutation({
    mutationFn: (variant: string) => api.startTakeoffJob(variant),
  });

export const useTakeoffRules = (params?: { element_type?: string; active?: string }) =>
  useQuery({
    queryKey: [...takeoffRulesQk, params],
    queryFn: () => api.listTakeoffRules(params),
  });

export const useTakeoffVariables = () =>
  useQuery({
    queryKey: ["est", "takeoff-variables"],
    queryFn: api.getTakeoffVariables,
    staleTime: 300_000,
  });

export const useCreateTakeoffRule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createTakeoffRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: takeoffRulesQk }),
  });
};

export const useUpdateTakeoffRule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.updateTakeoffRule>[1] }) =>
      api.updateTakeoffRule(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: takeoffRulesQk }),
  });
};

export const useDeleteTakeoffRule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteTakeoffRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: takeoffRulesQk }),
  });
};

/** Wycena przetargu — jedna wspólna dla wszystkich uprawnionych. */
export const useTenderTakeoffJob = (tender: string | undefined) =>
  useQuery({
    queryKey: ["est", "tender-job", tender ?? ""],
    queryFn: () => api.getTenderTakeoffJob(tender!),
    enabled: Boolean(tender),
  });

// ── Przeglądarka katalogów: normy KNR/KNNR + cenniki SEKOCENBUD ──────────────

/** Katalogi i cenniki zmieniają się raz na kwartał (import) — trzymamy je długo
 *  w cache, żeby chodzenie po drzewie nie odpytywało serwera w kółko. */
const CATALOG_STALE = 600_000;

export const useNormCatalogs = () =>
  useQuery({
    queryKey: ["est", "norm-catalogs"],
    queryFn: api.listNormCatalogs,
    staleTime: CATALOG_STALE,
  });

export const useNormFamilies = () =>
  useQuery({
    queryKey: ["est", "norm-families"],
    queryFn: api.listNormFamilies,
    staleTime: CATALOG_STALE,
  });

/** Tablice jednej księgi. Filtrowanie po tytule robimy po stronie klienta —
 *  księga ma najwyżej kilkaset tablic, więc reaguje od razu i bez zapytań. */
export const useNormTables = (family: string | null) =>
  useQuery({
    queryKey: ["est", "norm-tables", family ?? ""],
    queryFn: () => api.listNormTables(family!),
    enabled: Boolean(family),
    staleTime: CATALOG_STALE,
  });

export const useNormPositions = (
  params: api.NormPositionQuery,
  enabled = true,
) =>
  useQuery({
    queryKey: ["est", "norm-positions", params],
    queryFn: () => api.listNormPositions(params),
    enabled,
    staleTime: CATALOG_STALE,
    placeholderData: (prev) => prev,
  });

export const useNormPosition = (id: string | null) =>
  useQuery({
    queryKey: ["est", "norm-position", id ?? ""],
    queryFn: () => api.getNormPosition(id!),
    enabled: Boolean(id),
    staleTime: CATALOG_STALE,
  });

export const useCatalogEntries = (
  catalogId: string | null,
  params: api.CatalogEntryQuery,
) =>
  useQuery({
    queryKey: ["est", "catalog-entries", catalogId ?? "", params],
    queryFn: () => api.listCatalogEntries(catalogId!, params),
    enabled: Boolean(catalogId),
    staleTime: CATALOG_STALE,
    placeholderData: (prev) => prev,
  });

export const useCatalogStats = (catalogId: string | null) =>
  useQuery({
    queryKey: ["est", "catalog-stats", catalogId ?? ""],
    queryFn: () => api.getCatalogStats(catalogId!),
    enabled: Boolean(catalogId),
    staleTime: CATALOG_STALE,
  });
